/* eslint-disable no-useless-escape */
/* eslint-disable security/detect-unsafe-regex */
/* eslint-disable @typescript-eslint/no-unused-vars */

import hljs from 'highlight.js/lib/core';
import clike from 'highlight.js/lib/languages/c-like';
import 'highlight.js/styles/github.css';
import debounce from 'lodash/debounce';
import markdownit from 'markdown-it';
import markdownitEmoji from 'markdown-it-emoji';
import KeyCode from './KeyCode';

//declare let hljs: any;
// prerequisites:
//  hljs: syntax highlighting
//  window.markdownit: markdown rendering
//  window.markdownitEmoji: markdown emoji rendering

import { MarkdownToolbarHelper, MarkdownSyntax } from "./Utility";

hljs.registerLanguage("clike", clike);
hljs.registerLanguage("javascript", clike);
hljs.registerLanguage("js", clike);
hljs.registerLanguage("typescript", clike);
hljs.registerLanguage("ts", clike);
hljs.registerLanguage("c", clike);
hljs.registerLanguage("cpp", clike);
hljs.registerLanguage("csharp", clike);
hljs.registerLanguage("cs", clike);

// tslint:disable-next-line:interface-name
interface InsertOptions {
    requirePreSpace?: boolean; // ensure that whitespace is in front of the inserted text. For example, mentions require a space before the @ or #
    postString?: string; // extra text to be inserted after the cursor position. For example, links place the cursor inside different parts of the inserted text based on selection
}

export interface EditorOptions {
    /** Used for telemetry */
    formatInfoHandler: (action: string) => void;

    /**
     * Called to optionally modify the markdown text just before previewing.
     * Used for adding extra tags (e.g. [worked-in:vs 16.5] to preview output)
     */
    beforePreview?: ((markdown: string) => string);

    /**
     * Called when text changed.
     * Some actions e.g. bold/italic on Firefox will not trigger a textArea.input event, thus this one is used
     */
    textChanged: () => void;

    /**
     * Called when text changes from the default value.
     * textChanged gets called at many places even if text hasn't changed from default.
     * So use this better handle the modifications due to user input.
     */
    setDirty: () => void;

    /**
     * Called when caret is in a position where we can suggest items like list of persons (@), emojis (:), other workitems (#) etc.
     */
    suggestElement?: (elementType: MarkdownElementType, selectionStart?: number, selectionEnd?: number, filterText?: string) => void;

    /**
     * Called when special keys like Arrow Up/Down are pressed so that focus can be transferred to open suggetion lists if any
     * Focus and key stroke will be transferred to textarea if it i not hndled by host container
     */
    handleNavigationKey?: (keyCode: KeyCode) => boolean;
}

export enum MarkdownElementType {
    None = 0,
    Mention = 1,
    Emoji,
}

export class Minimark {
    private textArea: HTMLTextAreaElement;
    private preview: HTMLDivElement;
    private options: EditorOptions;
    private setPreviewHtml: (html: string) => void;

    private static md: any = null;

    constructor(textArea: HTMLTextAreaElement, preview: HTMLDivElement, options: EditorOptions, setPreviewHtml: (html: string) => void) {
        this.textArea = textArea;
        this.preview = preview;
        this.options = options;
        this.setPreviewHtml = setPreviewHtml;

        Minimark.InitMarkdownIfNeeded();

        this.textArea.oninput = () => { this.handleChange(); this.options.setDirty?.(); };

        // We need to handle both keyup and keydown because
        // keydown is needed in order to handle special keys like Ctrl+K (our shortcut for inserting link), Ctrl+O, special handling of arrow keys etc. where we want to override browser's default behavior.
        //     handling such keys in keyup doesn't work as browser's default action (e.g. go to address bar) happens between keydown and keyup calls
        // keyup is needed for  all other cases like invoking 'suggestElement' (used for @mentions) where it's more convinient to have the textarea already updated with incoming character
        this.textArea.onkeyup = (ev: KeyboardEvent) => { this.onKeyUp(ev); };
        this.textArea.onkeydown = (ev: KeyboardEvent) => { this.onKeyDown(ev); };

        this.textArea.onclick = () => { options?.suggestElement?.(null) };
        this.handleChange(); // render first time control shows up
    }

    private static InitMarkdownIfNeeded(): void {
        if (Minimark.md != null) {
            return;
        }

        Minimark.md = markdownit({
            html: false,
            breaks: true,
            linkify: true,
            typographer: true,
            highlight: (str: string, lang: string) => {
                if (lang && hljs.getLanguage(lang)) {
                    try {
                        return hljs.highlight(lang, str, true).value;
                        // eslint-disable-next-line no-empty
                    } catch (__) { }
                }
                else {
                    try {
                        return hljs.highlightAuto(str).value;
                        // eslint-disable-next-line no-empty
                    } catch (__) { }
                }
                return ''; // use external default escaping
            },
        });
        Minimark.md.linkify.set({ fuzzyLink: false });
        Minimark.md.use(markdownitEmoji);
        const defaultRender = Minimark.md.renderer.rules.link_open || function (tokens, idx, options, env, self) {
            return self.renderToken(tokens, idx, options);
        };
        const modifyAnchorTag = function (tokens, idx, options, env, self) {
            const aIndex = tokens[idx].attrIndex('rel');
            if (aIndex < 0) {
                tokens[idx].attrPush(['rel', 'ugc nofollow']);
            }
            return defaultRender(tokens, idx, options, env, self);
        };

        // Use the custom rule for link_open
        Minimark.md.renderer.rules.link_open = modifyAnchorTag;
    }

    public setBeforePreviewCallback(beforePreview: ((markdown: string) => string)): void {
        this.options.beforePreview = beforePreview;
    }

    private onKeyDown(e: KeyboardEvent): void {
        e = e || window.event as KeyboardEvent;
        const keyCode = e.which || e.keyCode || 0;
        if (e.ctrlKey !== true) {
            if (this.options.handleNavigationKey && keyCode === KeyCode.DOWN) {
                const handled = this.options.handleNavigationKey(keyCode);
                if (handled) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
            }

            return;
        }

        const keyMap: { [keyCode: number]: () => void } = {};
        keyMap[KeyCode.B] = () => { this.options.formatInfoHandler("bold"); this.bold(); };
        keyMap[KeyCode.I] = () => { this.options.formatInfoHandler("italic"); this.italic(); };

        keyMap[KeyCode.K] = () => { this.options.formatInfoHandler("link"); this.link(); };
        keyMap[KeyCode.Q] = () => { this.options.formatInfoHandler("quote"); this.quote(); };

        // keyMap[KeyCode.G] = () => { this.formatInfoHandler("image"); this.image(); };
        // keyMap[KeyCode.I] = () => { this.formatInfoHandler("attach"); this.attach(); };

        keyMap[KeyCode.O] = () => { this.options.formatInfoHandler("numberList"); this.numberList(); };
        keyMap[KeyCode.U] = () => { this.options.formatInfoHandler("bulletList"); this.bulletList(); };
        keyMap[KeyCode.H] = () => { this.options.formatInfoHandler("heading3"); this.heading3(); };

        // eslint-disable-next-line security/detect-object-injection
        const result: () => void | null = keyMap[keyCode];
        if (result == null) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        result();
    }

    private isSuggestVisible: boolean = false;

    private onKeyUp(e: KeyboardEvent): void {
        e = e || window.event as KeyboardEvent;
        const keyCode = e.which || e.keyCode || 0;
        if (e.ctrlKey === true) {
            return;
        }

        if (!this.options.suggestElement) {
            return;
        }

        const tokenBehindCaret = getTokenBehindCaret(this.textArea);
        const shouldCheckForPopups = [KeyCode.BACKSPACE, KeyCode.DELETE, KeyCode.SHIFT].indexOf(keyCode) !== -1 ||
            /^[0-9a-zA-Z-]$/.test(e.key) ||
            (keyCode === KeyCode.SPACE && this.isSuggestVisible && tokenBehindCaret?.text?.indexOf("  ") < 0); // if no two consecutive spaces and suggestion window is up, keep it up

        if (shouldCheckForPopups && tokenBehindCaret?.text?.startsWith("@")) {
            this.isSuggestVisible = true;
            this.options.suggestElement(MarkdownElementType.Mention, tokenBehindCaret.selectionStart, tokenBehindCaret.selectionEnd, tokenBehindCaret.text.substring(1, tokenBehindCaret.text.length));
        }
        else {
            this.isSuggestVisible = false;
            this.options.suggestElement(MarkdownElementType.None);
        }
    }

    public onSuggestCancelled(): void {
        this.isSuggestVisible = false;
    }

    public bold(): void {
        this.wrapFormat(MarkdownSyntax.Bold, "strong text");
    }

    public italic(): void {
        this.wrapFormat(MarkdownSyntax.Italic, "emphasized text");
    }

    public code(): void {
        const textArea = this.textArea;

        const selectionStart = textArea.selectionStart || 0;
        const selectionEnd = textArea.selectionEnd || 0;
        const currentText = textArea.value;
        const selectedText = currentText.substring(selectionStart, selectionEnd);
        // tslint:disable-next-line:triple-equals
        const codeType = (Minimark.isMultiline(selectedText) || (getCurrentLineText(textArea).trim() === "")) ? MarkdownSyntax.CodeBlock : MarkdownSyntax.Code;
        MarkdownToolbarHelper.wrapText(textArea, codeType);

        // getFormatHandler(editor, "`", "code", "`");
    }

    public heading3(): void {
        this.heading(MarkdownSyntax.Header3);
    }

    public heading(headerSyntax: string): void {
        MarkdownToolbarHelper.insertTextAtStartOfEachLine(this.textArea, headerSyntax, undefined, true, true);
    }

    public link(): void {
        const textArea = this.textArea;

        const currentText = textArea.value;
        let selectionStart = textArea.selectionStart;
        let selectionEnd = textArea.selectionEnd;
        let haveSelection = selectionStart !== selectionEnd;
        if (!haveSelection) {
            const expandResults = MarkdownToolbarHelper.expandCursorToWord(currentText, selectionStart);
            textArea.selectionStart = selectionStart = expandResults.start;
            textArea.selectionEnd = selectionEnd = expandResults.endExclusive;
            haveSelection = selectionStart !== selectionEnd;
        }
        else {
            // if we do have a selection, trim beginning/trailing whitespace
            // eslint-disable-next-line security/detect-object-injection
            while (selectionStart < selectionEnd && /\s/.test(currentText[selectionStart])) {
                selectionStart++;
            }

            while (selectionEnd > selectionStart && /\s/.test(currentText[selectionEnd - 1])) {
                selectionEnd--;
            }
        }

        let insertedString;
        let postString;

        // if we have a selection, wrap it inside [] and move the cursor to be inside ()
        // if we don't have a selection, put the cursor inside []
        if (haveSelection) {
            insertedString = "[" + currentText.substring(selectionStart, selectionEnd) + "](";
            postString = ")";
        }
        else {
            insertedString = "[";
            postString = "]()";
        }

        textArea.selectionStart = selectionStart;
        textArea.selectionEnd = selectionEnd;
        this.insertText(insertedString, { postString: postString });
    }

    public setText(text: string): void {
        this.textArea.value = text;
        this.handleChange();
        this.options.setDirty?.();
    }

    public getText(): string {
        return this.textArea.value;
    }

    public refresh(): void {
        this.handleChange();
    }

    public numberList(): void {
        MarkdownToolbarHelper.insertTextAtStartOfEachLine(this.textArea, MarkdownSyntax.NumberedList, undefined, true, false, true);
    }

    public mentionPerson(): void {
        MarkdownToolbarHelper.insertTextAtCursor(this.textArea, MarkdownSyntax.Mention);
        this.isSuggestVisible = true;
        this.options.suggestElement(MarkdownElementType.Mention, this.textArea.selectionStart, this.textArea.selectionEnd, "");
    }

    public bulletList(): void {
        MarkdownToolbarHelper.insertTextAtStartOfEachLine(this.textArea, MarkdownSyntax.BulletedList, undefined, true, false, true);
    }

    public quote(): void {
        MarkdownToolbarHelper.insertTextAtStartOfEachLine(this.textArea, MarkdownSyntax.Quote, undefined, true, false, true);
    }

    public focus(): void {
        this.textArea.focus();
    }

    public insertText(insertedString: string, options: InsertOptions,
        textChanged?: (text: string | null, selectionStart: number, selectionEnd: number) => void): void {
        const textArea = this.textArea;

        const currentText = textArea.value;
        const selectionStart = textArea.selectionStart;
        const selectionEnd = textArea.selectionEnd;
        let newText = insertedString;

        if (options.requirePreSpace) {
            // for some things like mentions, the preceding character needs to be whitespace
            if (selectionStart > 0 && !(/\s/.test(currentText[selectionStart - 1]))) {
                newText = " " + newText;
            }
        }

        textArea.focus();
        if (MarkdownToolbarHelper.browserSupportsInsertText()) {
            // If the browser supports it, insertText is a better way to programatically change the text which supports undo/redo
            // This will also trigger any events on the parent control as if the user typed in this text
            document.execCommand("insertText", false, newText + (options.postString || ""));
            if (options.postString) {
                let cursorPosition = textArea.selectionStart;
                cursorPosition -= options.postString.length;
                textArea.selectionStart = textArea.selectionEnd = cursorPosition;
            }
            if (textChanged) {
                textChanged(null, textArea.selectionStart, textArea.selectionEnd);
            }
        }
        else {
            const firstPart = currentText.substring(0, selectionStart);
            const endPart = currentText.substring(selectionEnd);
            const newCursorPosition = firstPart.length + newText.length;
            newText += options.postString || "";

            const allText: string = firstPart + newText + endPart;
            textArea.value = allText;
            textArea.selectionStart = textArea.selectionEnd = newCursorPosition;

            if (textChanged) {
                textChanged(allText, newCursorPosition, newCursorPosition);
            }
        }
    }

    private static isMultiline(str: string): boolean {
        const strAfterWhitespaceRemoval = str.trim();
        return strAfterWhitespaceRemoval.indexOf("\n") >= 0;
    }

    public static HtmlFromMarkdown(dangerousMarkdown: string): string {
        Minimark.InitMarkdownIfNeeded();
        return balanceTags(sanitizeHtml(Minimark.md.render(dangerousMarkdown)));
    }

    public static GuessHasMarkdownFormatting(text: string | null): boolean {
        if (text == null) {
            return false;
        }

        const linkRegex = /\[[^]]+\]\(https?:\/\/\S+\)/m;
        const generalMarkdown = /(\*\*)|(__)|(^###)|(```)/m;
        return text.match(linkRegex) != null ||
            text.match(generalMarkdown) != null;
    }

    private updatePreview = debounce(() => {
        const dangerousText: string = this.textArea.value || "";
        const previewText: string = this.options.beforePreview ? this.options.beforePreview(dangerousText) : dangerousText;
        this.setPreviewHtml(Minimark.HtmlFromMarkdown(previewText));
    }, 800);

    private handleChange(): void {
        this.options.textChanged();
        this.updatePreview();
    }

    private wrapFormat(prepend: string, placeholder: string): void {
        MarkdownToolbarHelper.wrapText(this.textArea, prepend);
        this.handleChange();
        this.options.setDirty?.();
        // format(editor, prepend, placeholder, append);
    }
}

function getTokenBehindCaret(textArea: HTMLTextAreaElement): { text: string; selectionStart: number; selectionEnd: number } {
    if (textArea.selectionStart === textArea.selectionEnd) {
        const regex = /(?:^|\s+)([@][a-zA-Z0-9- ]*)$/g;
        const cursorPos = textArea.selectionStart;
        const text = textArea.value;

        const textUptoCaret = text.substring(0, cursorPos);
        const match = regex.exec(textUptoCaret);
        const token = match?.[1];
        if (token) {
            return { text: token, selectionStart: cursorPos - token.length, selectionEnd: cursorPos };
        }
    }
    return null;
}

export function getCurrentLineText(textArea: HTMLTextAreaElement): string {
    const cursorPos: number = textArea.selectionStart;
    const text: string = textArea.value;
    const linesBeforeCursor: string[] = text.substr(0, cursorPos).split("\n");

    const lineStart: number = cursorPos - linesBeforeCursor[linesBeforeCursor.length - 1].length;
    let lineEnd: number = lineStart;
    const len = text.length;
    while (lineEnd < len) {
        // eslint-disable-next-line security/detect-object-injection
        if (text[lineEnd] === '\r' || text[lineEnd] === '\n') {
            break;
        }
        lineEnd++;
    }

    return text.substring(lineStart, lineEnd);
}

function formatLineStart(editor: HTMLTextAreaElement, value: string): void {
    const cursorPos: number = editor.selectionStart;
    const oldText: string = editor.value;
    const linesBeforeCursor: string[] = oldText.substr(0, cursorPos).split("\n");

    const lineStart: number = cursorPos - linesBeforeCursor[linesBeforeCursor.length - 1].length;

    editor.value = oldText.substring(0, lineStart) + value + oldText.substring(lineStart);
    editor.focus(); // needed on Chrome for selection to work

    // update cursor position
    editor.selectionStart = cursorPos + value.length;
    editor.selectionEnd = cursorPos + value.length;
}

function format(editor: HTMLTextAreaElement, prepend: string, placeholder: string, append: string): void {
    const start: number = editor.selectionStart;
    const end: number = editor.selectionEnd;
    const oldText: string = editor.value;
    if (start === end) { // if no selection
        const cursorPos: number = start;
        editor.value = oldText.substring(0, cursorPos) + prepend + placeholder + append + oldText.substring(cursorPos);
        editor.focus(); // needed on Chrome for selection to work
        editor.selectionStart = cursorPos + prepend.length;
        editor.selectionEnd = cursorPos + prepend.length + placeholder.length;
    }
    else { // has selection
        editor.value = oldText.substring(0, start) + prepend + oldText.substring(start, end) + append + oldText.substring(end);
        editor.focus(); // needed on Chrome for selection to work
        editor.selectionStart = start + prepend.length;
        editor.selectionEnd = end + prepend.length;
    }
}

function insertAtCursor(editor: HTMLTextAreaElement, value: string): void {
    // IE support
    if ((document as any).selection) {
        editor.focus();
        const sel = (document as any).selection.createRange();
        sel.text = value;
    }
    // MOZILLA and others
    // tslint:disable-next-line:triple-equals
    else if (editor.selectionStart || editor.selectionStart === 0) {
        const startPos: number = editor.selectionStart;
        const endPos: number = editor.selectionEnd;
        editor.value = editor.value.substring(0, startPos)
            + value
            + editor.value.substring(endPos, editor.value.length);
    } else {
        editor.value += value;
    }
}

//https://github.com/azaslavsky/TextStack

function sanitizeHtml(html: string): string {
    return html.replace(/<[^>]*>?/gi, sanitizeTag);
}

////// From WMD editor (StackOverflow), MIT license: https://code.google.com/archive/p/pagedown/source/default/source
// (tags that can be opened/closed) | (tags that stand alone)
const basicTagAllowList = /^(<\/?(b|blockquote|code|del|dd|dl|dt|em|h1|h2|h3|h4|h5|h6|i|kbd|li|ol(?: start="\d+")?|p|pre|s|span|sup|sub|strong|strike|table|thead|tbody|td|th|tr|ul)>|<(br|hr)\s?\/?>)$/i;
// <a href="url..." optional title>|</a>
const aWhite = /^(<a\shref="((https?|ftp):\/\/|\/)[-A-Za-z0-9+&@#\/%?=~_|!:,.;\(\)*[\]$]+"(\stitle="[^"<>]+")?(?:\s+rel="ugc nofollow")?\s?>|<\/a>)$/i;

// <img src="url..." optional width  optional height  optional alt  optional title
const imgWhite = /^(<img\ssrc="(https?:\/\/|\/)[-A-Za-z0-9+&@#\/%?=~_|!:,.;\(\)*[\]$]+"(\swidth="\d{1,3}")?(\sheight="\d{1,3}")?(\salt="[^"<>]*")?(\stitle="[^"<>]*")?\s?\/?>)$/i;

// <td or <th with optional align
const tdthAlign = /^<(td|th)(\sstyle="text-align:(left|right|center)?")?>$/i;

// syntax highlight span, e.g. <span class="hljs-built_in">
const spanHighlight = /^<span\sclass="hljs-(\w|-)+">$/i;

// syntax highlight code tag, e.g. <code class="language-javascript">
const codeHighlight = /^<code\sclass="language-(\w|-)+">$/i;

function sanitizeTag(tag: string): string {
    if (tag.match(basicTagAllowList) ||
        tag.match(aWhite) ||
        tag.match(imgWhite) ||
        tag.match(tdthAlign) ||
        tag.match(spanHighlight) ||
        tag.match(codeHighlight)
    ) {
        return tag;
    }

    return "";
}

/// <summary>
/// attempt to balance HTML tags in the html string
/// by removing any unmatched opening or closing tags
/// IMPORTANT: we *assume* HTML has *already* been
/// sanitized and is safe/sane before balancing!
///
/// adapted from CODESNIPPET: A8591DBA-D1D3-11DE-947C-BA5556D89593
/// </summary>
function balanceTags(html: string): string {
    // tslint:disable-next-line:triple-equals
    if (html === "") {
        return "";
    }

    const re = /<\/?\w+[^>]*(\s|$|>)/g;
    // convert everything to lower case; this makes
    // our case insensitive comparisons easier
    const tags = html.toLowerCase().match(re);

    // no HTML tags present? nothing to do; exit now
    const tagcount = (tags || []).length;
    if (tagcount === 0) {
        return html;
    }

    let tagname: string;
    let tag: string;
    const ignoredtags = "<p><img><br><li><hr>";
    let match: number;
    const tagpaired: boolean[] = [];
    const tagremove: boolean[] = [];
    let needsRemoval = false;

    if (tags != null) {
        // loop through matched tags in forward order
        for (let ctag = 0; ctag < tagcount; ctag++) {
            // eslint-disable-next-line security/detect-object-injection
            tagname = tags[ctag].replace(/<\/?(\w+).*/, "$1");
            // skip any already paired tags
            // and skip tags in our ignore list; assume they're self-closed
            // eslint-disable-next-line security/detect-object-injection
            if (tagpaired[ctag] || ignoredtags.search("<" + tagname + ">") > -1) {
                continue;
            }

            // eslint-disable-next-line security/detect-object-injection
            tag = tags[ctag];
            match = -1;

            if (!/^<\//.test(tag)) {
                // this is an opening tag
                // search forwards (next tags), look for closing tags
                for (let ntag = ctag + 1; ntag < tagcount; ntag++) {
                    // eslint-disable-next-line security/detect-object-injection
                    if (!tagpaired[ntag] && tags[ntag] === "</" + tagname + ">") {
                        match = ntag;
                        break;
                    }
                }
            }

            if (match === -1) {
                // eslint-disable-next-line security/detect-object-injection
                needsRemoval = tagremove[ctag] = true; // mark for removal
            }
            else {
                // eslint-disable-next-line security/detect-object-injection
                tagpaired[match] = true; // mark paired
            }
        }
    }

    if (!needsRemoval) {
        return html;
    }

    // delete all orphaned tags from the string

    let tagIndex = 0;
    html = html.replace(re, (matchStr: string) => {
        // eslint-disable-next-line security/detect-object-injection
        const res = tagremove[tagIndex] ? "" : matchStr;
        tagIndex++;
        return res;
    });
    return html;
}

